export const USER_PARTY_RELATION_TYPES = [
  'Self',
  'Buyer',
  'SellerOwner',
  'SellerOperator',
  'AdminOperator',
  'Employee',
  'Agent',
  'FunderOperator'
] as const;

export const USER_PARTY_BINDING_STATUSES = [
  'PendingVerification',
  'Active',
  'Suspended',
  'Revoked'
] as const;

export type UserPartyRelationTypeName = typeof USER_PARTY_RELATION_TYPES[number];
export type UserPartyBindingStatusName = typeof USER_PARTY_BINDING_STATUSES[number];

const RELATION_TYPE_NUMERIC: Record<UserPartyRelationTypeName, number> = {
  Self: 0,
  Buyer: 1,
  SellerOwner: 2,
  SellerOperator: 3,
  AdminOperator: 4,
  Employee: 5,
  Agent: 6,
  FunderOperator: 7
};

const STATUS_NUMERIC: Record<UserPartyBindingStatusName, number> = {
  PendingVerification: 0,
  Active: 1,
  Suspended: 2,
  Revoked: 3
};

export function normalizeRelationType(value: number | string | null | undefined): UserPartyRelationTypeName | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (typeof value === 'number' || (typeof value === 'string' && /^\d+$/.test(value))) {
    const numeric = typeof value === 'number' ? value : Number(value);
    return USER_PARTY_RELATION_TYPES[numeric] ?? null;
  }

  const normalized = `${value}`;
  return USER_PARTY_RELATION_TYPES.find(item => item.toLowerCase() === normalized.toLowerCase()) ?? null;
}

export function normalizeBindingStatus(value: number | string | null | undefined): UserPartyBindingStatusName | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (typeof value === 'number' || (typeof value === 'string' && /^\d+$/.test(value))) {
    const numeric = typeof value === 'number' ? value : Number(value);
    return USER_PARTY_BINDING_STATUSES[numeric] ?? null;
  }

  const normalized = `${value}`;
  return USER_PARTY_BINDING_STATUSES.find(item => item.toLowerCase() === normalized.toLowerCase()) ?? null;
}

export function relationTypeLabelKey(value: number | string): string {
  const normalized = normalizeRelationType(value);
  if (!normalized) {
    return '';
  }
  return `platform.bindings.relationType.${normalized.charAt(0).toLowerCase()}${normalized.slice(1)}`;
}

export function bindingStatusLabelKey(value: number | string): string {
  const normalized = normalizeBindingStatus(value);
  if (!normalized) {
    return '';
  }
  return `platform.bindings.status.${normalized.charAt(0).toLowerCase()}${normalized.slice(1)}`;
}

export function bindingStatusSeverity(value: number | string): 'success' | 'warn' | 'danger' | 'secondary' {
  const normalized = normalizeBindingStatus(value);
  switch (normalized) {
    case 'Active':
      return 'success';
    case 'PendingVerification':
      return 'secondary';
    case 'Suspended':
      return 'warn';
    case 'Revoked':
      return 'danger';
    default:
      return 'secondary';
  }
}

export function relationTypeApiValue(value: UserPartyRelationTypeName): number {
  return RELATION_TYPE_NUMERIC[value];
}

export function bindingStatusApiValue(value: UserPartyBindingStatusName): number {
  return STATUS_NUMERIC[value];
}

export function isActiveBindingStatus(value: number | string): boolean {
  return normalizeBindingStatus(value) === 'Active';
}

export function isBuyerRelation(value: number | string): boolean {
  const normalized = normalizeRelationType(value);
  return normalized === 'Buyer' || normalized === 'Self';
}

export function isSellerRelation(value: number | string): boolean {
  const normalized = normalizeRelationType(value);
  return normalized === 'SellerOwner' || normalized === 'SellerOperator';
}
